import { StrictMode } from 'react'
import type { ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router-dom'
import './index.css'
import { ApiError } from './api/client'
import { AuthProvider, useAuth } from './lib/auth'
import { applyTheme, readTheme } from './lib/theme'
import { Shell } from './components/Shell'
import { Empty, Spinner } from './components/ui'
import { AllTasks } from './pages/AllTasks'
import { Approvals } from './pages/Approvals'
import { CalendarPage } from './pages/Calendar'
import { Channel } from './pages/Channel'
import { Timezone } from './pages/Timezone'
import { DayClose } from './pages/DayClose'
import { MasterData } from './pages/MasterData'
import { NewTasks } from './pages/NewTasks'
import { Recurring } from './pages/Recurring'
import { Reminders } from './pages/Reminders'
import { Explore } from './pages/Explore'
import { Insights } from './pages/Insights'
import { System } from './pages/System'
import { Team } from './pages/Team'
import { Reports } from './pages/Reports'
import { SignIn } from './pages/SignIn'
import { Today } from './pages/Today'
import { Notices } from './pages/Notices'

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      refetchOnWindowFocus: true,
      retry: (count, error) => {
        // Never retry what will not change: auth, permissions, validation, or a
        // stale write. Only transient failures are worth a second attempt.
        const e = error as ApiError
        if (e instanceof ApiError && (e.status < 500 || e.isConflict)) return false
        return count < 2
      },
    },
  },
})

/** An Owner-only screen, for somebody who is not an Owner.
 *
 *  The nav hides these links, but nothing stopped a typed URL, a bookmark, or a link
 *  pasted into a chat — so `/recurring` rendered a full empty shell with live-looking
 *  "+ New rule" and "Generate due tasks now" buttons, and `/master` showed the API's
 *  raw refusal: "This needs one of: OWNER. req_01M19VR4S6…". The data was never at
 *  risk — every endpoint behind these screens is `owner_only` — but a person who sees
 *  an internal role name and a request id concludes the software is broken rather
 *  than that they lack a permission. */
/** The System screen belongs to the superuser: they are the one the alerts are sent
 *  to. `is_owner` would be wrong - there are several owners and this is not all of
 *  their business - but the endpoints behind it stay `owner_only`, so another owner
 *  debugging can still reach the data directly. This gate is about whose screen it
 *  is, not about who may see the data. */
function SuperuserOnly({ children }: { children: ReactNode }) {
  const { me } = useAuth()
  if (me?.is_superuser) return <>{children}</>
  return (
    <Empty
      title="This is the superuser's screen"
      hint="System alerts go to whoever holds the superuser account."
      action={<Link to="/today" className="btn btn-primary">Back to today</Link>} />
  )
}

/** Owner, or a manager the Owner has given task entry. The route still 403s
 *  server-side; this only decides whether to render a screen or an explanation. */
function OwnerOrManager({ children }: { children: ReactNode }) {
  const { me } = useAuth()
  if (me?.is_owner || me?.may_enter_tasks) return <>{children}</>
  return (
    <Empty
      title="This screen writes the day's list"
      hint="Owners write it for the firm, and managers for their own team. Ask an
            owner if you should be able to."
      action={<Link to="/today" className="btn btn-primary">Back to today</Link>} />
  )
}

/** Anybody the server would let read reports - approvers and owners.
 *
 *  Typed straight into the address bar these rendered a screen that then filled with
 *  403s, which reads as the app being broken rather than as the screen not being
 *  theirs. `permissions` is the server's own answer, so this cannot drift from it. */
function CanSeeReports({ children }: { children: ReactNode }) {
  const { me } = useAuth()
  if (me?.permissions?.reports) return <>{children}</>
  return (
    <Empty
      title="Reports are for approvers"
      hint="They cover other people's work as well as your own. Today, All tasks and
            Carried forward are yours."
      action={<Link to="/today" className="btn btn-primary">Back to today</Link>} />
  )
}

function CanApprove({ children }: { children: ReactNode }) {
  const { me } = useAuth()
  if (me?.permissions?.approve) return <>{children}</>
  return (
    <Empty
      title="Nothing waits on you"
      hint="The approval queue belongs to whoever approves work. Your own tasks are
            on Today."
      action={<Link to="/today" className="btn btn-primary">Back to today</Link>} />
  )
}

function OwnerOnly({ children }: { children: ReactNode }) {
  const { me } = useAuth()
  if (me?.is_owner) return <>{children}</>
  return (
    <Empty
      title="This is an Owner screen"
      hint="Your account does not manage the firm's settings. Today, All tasks,
            Approvals and Carried forward are yours."
      action={<Link to="/today" className="btn btn-primary">Back to today</Link>} />
  )
}

function Gate() {
  const { me, ready } = useAuth()
  if (!ready) return <div className="min-h-screen grid place-items-center"><Spinner label="Restoring your session" /></div>
  if (!me) return <SignIn />
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/today" element={<Today />} />
        {/* No gate. The first screen in this app that belongs to everybody - every
            other one is for whoever writes the list, decides approvals or reads
            reports, and a responsible person could see their tasks and nothing
            else. */}
        <Route path="/notices" element={<Notices />} />
        <Route path="/tasks" element={<AllTasks />} />
        <Route path="/new" element={<OwnerOrManager><NewTasks /></OwnerOrManager>} />
        <Route path="/approvals" element={<CanApprove><Approvals /></CanApprove>} />
        <Route path="/carried" element={<AllTasks carriedOnly />} />
        <Route path="/day-close" element={<OwnerOnly><DayClose /></OwnerOnly>} />
        <Route path="/recurring" element={<OwnerOnly><Recurring /></OwnerOnly>} />
        <Route path="/reminders" element={<OwnerOnly><Reminders /></OwnerOnly>} />
        <Route path="/channel"
               element={<SuperuserOnly><Channel /></SuperuserOnly>} />
        <Route path="/timezone"
               element={<SuperuserOnly><Timezone /></SuperuserOnly>} />
        <Route path="/calendar" element={<OwnerOnly><CalendarPage /></OwnerOnly>} />
        <Route path="/master" element={<OwnerOnly><MasterData /></OwnerOnly>} />
        {/* The screen used to be People & companies; a bookmark should still land. */}
        <Route path="/people" element={<Navigate to="/master" replace />} />
        <Route path="/reports"
               element={<CanSeeReports><Reports /></CanSeeReports>} />
        <Route path="/explore"
               element={<CanSeeReports><Explore /></CanSeeReports>} />
        <Route path="/insights"
               element={<CanSeeReports><Insights /></CanSeeReports>} />
        <Route path="/team" element={<OwnerOnly><Team /></OwnerOnly>} />
        <Route path="/system" element={<SuperuserOnly><System /></SuperuserOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/today" replace />} />
    </Routes>
  )
}

// Before React mounts, so a stored theme is never preceded by a frame of the default
// one. Cheap - one attribute and one localStorage read.
applyTheme(readTheme())

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <AuthProvider><Gate /></AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
